import { Injectable } from '@angular/core';
import { ModalController, Platform } from '@ionic/angular';
import { UserService } from './user.service';
import { FeatureHighlightComponent } from '../components/feature-highlight/feature-highlight.component';

const STORAGE_KEY_PREFIX = 'et-feature-highlight';

export interface FeatureHighlightOptions {
  title: string;
  description: string;
  videoUrl?: string;
  icon?: string;
  /** When true, always show and do not mark as seen (for testing). */
  bypassStorage?: boolean;
}

@Injectable({ providedIn: 'root' })
export class FeatureHighlightService {
  constructor(
    private modalCtrl: ModalController,
    private platform: Platform,
    private userService: UserService
  ) {}

  /**
   * Returns true if this feature has not been shown yet for the current user.
   */
  shouldShow(featureId: string): boolean {
    const key = this.storageKey(featureId);
    try {
      return localStorage.getItem(key) !== '1';
    } catch {
      return true;
    }
  }

  /**
   * Presents the feature highlight modal only if the user has not seen it before.
   * Call this when a page loads (e.g. after data is ready).
   * Pass options.bypassStorage = true to always show (for testing).
   */
  async showIfFirstTime(featureId: string, options: FeatureHighlightOptions): Promise<void> {
    if (!options.bypassStorage && !this.shouldShow(featureId)) {
      return;
    }
    await this.show(featureId, options);
  }

  /**
   * Always shows the feature highlight (e.g. from a "How does this work?" link).
   * Sheet/drawer on mobile; centered dialog on desktop/web.
   */
  async show(featureId: string, options: FeatureHighlightOptions): Promise<void> {
    const useSheet = this.shouldUseSheetPresentation();
    const modal = await this.modalCtrl.create({
      component: FeatureHighlightComponent,
      cssClass: useSheet
        ? 'feature-highlight-modal'
        : 'feature-highlight-modal feature-highlight-modal--dialog auto-height',
      componentProps: {
        title: options.title,
        description: options.description,
        videoUrl: options.videoUrl,
        icon: options.icon || 'sparkles-outline',
      },
      ...(useSheet
        ? {
            breakpoints: [0, 0.5, 0.85],
            initialBreakpoint: 0.85,
          }
        : {
            backdropDismiss: true,
          }),
    });
    await modal.present();
    const { data } = await modal.onWillDismiss();
    if (data?.seen && !options.bypassStorage) {
      this.markAsSeen(featureId);
    }
  }

  markAsSeen(featureId: string): void {
    const key = this.storageKey(featureId);
    try {
      localStorage.setItem(key, '1');
    } catch {}
  }

  private shouldUseSheetPresentation(): boolean {
    if (typeof window !== 'undefined' && window.innerWidth >= 768) {
      return false;
    }
    return this.platform.is('mobile') || this.platform.is('mobileweb') || this.platform.is('hybrid');
  }

  private storageKey(featureId: string): string {
    const userId = this.userService.getAppUser()?.uid || 'anonymous';
    return `${STORAGE_KEY_PREFIX}-${userId}-${featureId}`;
  }
}
